/**
 * Sorting a list of customers to pick from: by name, or newest first — so customers added
 * this week (who usually still need a plan, an area or an agent) are easy to find.
 * Shared by the product's Add Customers screen and the area / agent add-customer screens;
 * the control is the app's <SortSelect> beside the search box.
 */

export const SORT_NAME = 'name';
export const SORT_NEWEST = 'newest';
export const SORT_AREA = 'area';
export const SORT_AGENT = 'agent';

const addedAt = (c) => (c.f_inserted_at ? new Date(c.f_inserted_at).getTime() : 0);

const byName = (a, b) => (a.f_name || '').localeCompare(b.f_name || '');

/**
 * A sorted copy.
 *   newest  most recently added first (ties by id, so the order is stable)
 *   name    A–Z
 *   area    area by area in route order (areaRankOf), no area last; within an area, the
 *           delivery order — how the round is walked
 *   agent   agent by agent (agentOf gives the name), no agent last; then by name
 * get: { areaRankOf(c) -> number | null, agentOf(c) -> name | null }
 */
export function sortCustomers(list, mode, get = {}) {
    const copy = [...list];
    if (mode === SORT_NEWEST) {
        return copy.sort((a, b) => addedAt(b) - addedAt(a) || (b.f_id || 0) - (a.f_id || 0));
    }
    if (mode === SORT_AREA && get.areaRankOf) {
        const rank = (c) => { const r = get.areaRankOf(c); return r == null ? Infinity : r; };
        return copy.sort((a, b) => rank(a) - rank(b)
            || (a.f_sort_order ?? 0) - (b.f_sort_order ?? 0) || byName(a, b));
    }
    if (mode === SORT_AGENT && get.agentOf) {
        const name = (c) => get.agentOf(c) || '';
        return copy.sort((a, b) => (!name(a)) - (!name(b))
            || name(a).localeCompare(name(b)) || byName(a, b));
    }
    return copy.sort(byName);
}

/** "Added today" / "Added yesterday" / "Added 12 Oct" — shown on rows when sorted newest first. */
export function addedLabel(c) {
    if (!c.f_inserted_at) return null;
    const d = new Date(c.f_inserted_at);
    const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const diff = Math.round((day(new Date()) - day(d)) / 86400000);
    if (diff === 0) return 'Added today';
    if (diff === 1) return 'Added yesterday';
    return `Added ${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
}

/** Options for <SortSelect> — the same wording as the Customers page's Sort by. */
// First = the default: newest first, so customers just added (who still need setting up) lead.
export const CUSTOMER_SORT_OPTIONS = [
    { value: SORT_NEWEST, label: 'Added ↓ (Newest)', short: 'Added ↓' },
    { value: SORT_NAME, label: 'Name (A–Z)', short: 'Name' },
    { value: SORT_AREA, label: 'Area (route order)', short: 'Area' },
    { value: SORT_AGENT, label: 'Agent', short: 'Agent' },
];
